import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';

const OmDocumentUpload = forwardRef(({
  progress = 0,
  placeholder = "Upload Document",
  secondaryPlaceholder = "PDF or JPG. Max 10 MB",
  acceptedFileTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'application/pdf'],
  maxFileSizeInBytes = 10485760, // 10 MB default
  customErrorMessage = "",
  disabled = false,
  uploadFunction,
  cancelUpload,
  deleteFile,
  removeFileOnError,
  onFileSelect,
  onError,
  className = "",
  style = {},
  ...props
}, ref) => {
  const documentUploadRef = useRef(null);

  useImperativeHandle(ref, () => ({
    focus: () => documentUploadRef.current?.focus(),
    blur: () => documentUploadRef.current?.blur(),
    getElement: () => documentUploadRef.current
  }));

  useEffect(() => {
    const element = documentUploadRef.current;
    if (!element) return;

    // Wait for the element to be fully initialized before setting properties
    const initializeElement = () => {
      // Set the acceptedFileTypes property directly on the element
      if (acceptedFileTypes && Array.isArray(acceptedFileTypes)) {
        element.acceptedFileTypes = acceptedFileTypes;
      } else if (typeof acceptedFileTypes === 'string') {
        element.acceptedFileTypes = acceptedFileTypes.split(',').map(type => type.trim());
      }

      // Set the uploadFunction property directly on the element
      if (uploadFunction) {
        element.uploadFunction = uploadFunction;
      }

      // Set callback functions
      if (cancelUpload) {
        element.cancelUpload = cancelUpload;
      }
      if (deleteFile) {
        element.deleteFile = deleteFile;
      }
      if (removeFileOnError) {
        element.removeFileOnError = removeFileOnError;
      }
    };

    // Check if the element is ready, if not wait for it
    if (element.isConnected && element.shadowRoot) {
      initializeElement();
    } else {
      // Wait for the component to be fully connected and initialized
      // DISABLED: MutationObserver to test if it's causing React DOM race conditions
      /*
      const observer = new MutationObserver(() => {
        console.log('🔴 MUTATION_OBSERVER: DOM mutation detected', {
          isConnected: element.isConnected,
          hasShadowRoot: !!element.shadowRoot,
          timestamp: Date.now()
        });
        if (element.isConnected && element.shadowRoot) {
          console.log('🔴 MUTATION_OBSERVER: About to call initializeElement');
          initializeElement();
          observer.disconnect();
        }
      });
      observer.observe(element, { childList: true, subtree: true });
      */
      
      // Fallback timeout in case MutationObserver doesn't catch it
      setTimeout(() => {
        console.log('🔴 TIMEOUT_FALLBACK: Timeout triggered', {
          isConnected: element.isConnected,
          timestamp: Date.now()
        });
        if (element.isConnected) {
          initializeElement();
          // observer.disconnect(); // Commented out since observer is disabled
        }
      }, 100);
    }

    // Listen for the correct events (omkFileSelected/omkFileError)
    const handleFileSelected = (event) => {
      console.log('🟢 OmDocumentUpload: omkFileSelected event fired', {
        eventType: event.type,
        eventDetail: event.detail,
        file: event.detail,
        hasOnFileSelect: !!onFileSelect
      });
      if (onFileSelect) {
        const file = event.detail;
        onFileSelect(file, event);
      }
    };

    const handleFileError = (event) => {
      console.log('🔴 OmDocumentUpload: omkFileError event fired', {
        eventType: event.type,
        eventDetail: event.detail,
        error: event.detail,
        hasOnError: !!onError
      });
      if (onError) {
        const error = event.detail;
        onError(error, event);
      }
    };

    // Add event listeners for the actual events
    element.addEventListener('omkFileSelected', handleFileSelected);
    element.addEventListener('omkFileError', handleFileError);

    // Cleanup function
    return () => {
      element.removeEventListener('omkFileSelected', handleFileSelected);
      element.removeEventListener('omkFileError', handleFileError);
    };
  }, [uploadFunction, cancelUpload, deleteFile, removeFileOnError, onFileSelect, onError, acceptedFileTypes]);

  return (
    <omk-document-upload
      ref={documentUploadRef}
      progress={progress.toString()}
      placeholder={placeholder}
      secondary-placeholder={secondaryPlaceholder}
      max-file-size-in-bytes={maxFileSizeInBytes.toString()}
      custom-error-message={customErrorMessage}
      disabled={disabled ? 'true' : undefined}
      className={className}
      style={style}
      {...props}
    />
  );
});

OmDocumentUpload.displayName = 'OmDocumentUpload';

export default OmDocumentUpload;
